import Container from '@/container';
import {Col, Grid, Row} from '@/grid';
import {withMarginBottomXs, withNoMarginBottom} from '@/hocs/withSpacing';
import {H2, H3, P} from '@/typography';
import PropTypes from 'prop-types';
import {useId, useState} from 'react';
import FaqRightShape from '@/decorative/faq';
import styles from './styles.module.scss';

// Shapes

const Question = withMarginBottomXs(H3);
const QuestionAccordion = withNoMarginBottom(H3);

const FAQ_VARIANTS = {
  ACCORDION: 'accordion',
  DEFAULT: 'default'
};

const propTypes = {
  title: PropTypes.string,
  questions: PropTypes.arrayOf(
    PropTypes.shape({
      question: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
      answer: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired
    })
  ),
  schema: PropTypes.bool,
  variant: PropTypes.oneOf([FAQ_VARIANTS.ACCORDION, FAQ_VARIANTS.DEFAULT, null]),
  shape: PropTypes.node,
  isSafe: PropTypes.bool,
  arrow: PropTypes.bool
};

const Faq = ({
  title = '',
  questions = [],
  schema = false,
  variant = null,
  shape = null,
  isSafe = false,
  arrow = false
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  // Scopes ids to this instance; strip `:` so they stay CSS/querySelector-friendly
  const instanceId = useId().replace(/[^a-zA-Z0-9_-]/g, '');

  if (schema) {
    const faqSchema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: []
    };

    questions.forEach(question => {
      faqSchema.mainEntity.push({
        '@type': 'Question',
        name: question.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: question.answer
        }
      });
    });

    return (
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(faqSchema)
        }}
        key="faq-jsonld"
      />
    );
  }

  const getShape = () => {
    if (shape) {
      return shape;
    }

    return <FaqRightShape />;
  };
  const toggleAccordion = index => {
    setActiveIndex(prevIndex => (prevIndex === index ? null : index));
  };

  const handleQuestionKeyDown = (event, index) => {
    if (event.repeat) {
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleAccordion(index);
    }
  };

  return (
    <Container>
      <Grid fluid>
        <Row>
          <Col xs={12} lg={shape ? 7 : 12}>
            {title && <H2>{title}</H2>}

            <div className={styles[`faq--${variant}`]}>
              {questions.map((question, index) => (
                <div
                  // eslint-disable-next-line react/no-array-index-key
                  key={index}
                  className={`${styles.faq__item} ${
                    variant === FAQ_VARIANTS.ACCORDION && activeIndex === index
                      ? styles['faq__item--active']
                      : ''
                  }`}>
                  {variant === FAQ_VARIANTS.ACCORDION ? (
                    <div>
                      <div
                        role="button"
                        aria-expanded={activeIndex === index}
                        aria-controls={`faq-answer-${instanceId}-${index}`}
                        id={`faq-question-${instanceId}-${index}`}
                        tabIndex={0}
                        className={styles.faq__question}
                        onClick={() => toggleAccordion(index)}
                        onKeyDown={event => handleQuestionKeyDown(event, index)}>
                        <QuestionAccordion>{question.question}</QuestionAccordion>
                        <span
                          className={`${arrow ? styles.faq__arrow : styles.faq__button} ${activeIndex === index ? styles.open : ''}`}
                        />
                      </div>
                      <div
                        className={styles.faq__answer}
                        id={`faq-answer-${instanceId}-${index}`}
                        role="region"
                        aria-labelledby={`faq-question-${instanceId}-${index}`}>
                        {isSafe ? (
                          <div className={styles['faq__answer-inner']}>{question.answer}</div>
                        ) : (
                          <div
                            className={styles['faq__answer-inner']}
                            // eslint-disable-next-line react/no-danger
                            dangerouslySetInnerHTML={{__html: question.answer}}
                          />
                        )}
                      </div>
                    </div>
                  ) : (
                    <div>
                      <Question>{question.question}</Question>
                      <P>{question.answer}</P>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Col>
          {shape && (
            <Col xs={12} lg={4} lgOffset={1}>
              {getShape()}
            </Col>
          )}
        </Row>
      </Grid>
    </Container>
  );
};

Faq.propTypes = propTypes;

export default Faq;
